'use client';

import { CrudDataGrid, type CrudGridColDef } from '@andritzot/metris-web-ui/mui-x/crud-data-grid';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';

import { useRoadmapHistory } from '@/domain/roadmap/hooks';
import { type History } from '@/domain/roadmap/types';
import { toLocaleDateTimeString } from '@/utils/datetime';

import { historyFields } from '../../HistoryTable/historyFields';

interface HistoryLogTabContentProps {
  roadmapId: number | undefined;
  isLoading?: boolean;
}

const columns: CrudGridColDef<History>[] = [
  {
    field: 'user',
    headerName: 'User',
    sortable: false,
    flex: 1,
    renderCell: ({ row }) => row.user?.name ?? '—',
  },
  {
    field: 'timestamp',
    headerName: 'Date',
    sortable: true,
    width: 180,
    renderCell: ({ row }) => (row.timestamp ? toLocaleDateTimeString(row.timestamp) : '—'),
  },
  {
    field: 'field',
    headerName: 'Field',
    sortable: true,
    flex: 1,
    renderCell: ({ row }) => historyFields[row.field] ?? row.field,
  },
  {
    field: 'originalValue',
    headerName: 'Previous Value',
    sortable: false,
    flex: 1,
    renderCell: ({ row }) => row.originalValue ?? '—',
  },
  {
    field: 'newValue',
    headerName: 'New Value',
    sortable: false,
    flex: 1,
    renderCell: ({ row }) => row.newValue ?? '—',
  },
];

export function HistoryLogTabContent({ roadmapId, isLoading: isRoadmapLoading }: Readonly<HistoryLogTabContentProps>) {
  const { data: history, isLoading: isHistoryLoading } = useRoadmapHistory(roadmapId ?? 0);
  const isLoading = isRoadmapLoading || isHistoryLoading;

  if (!roadmapId && !isLoading) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Select a roadmap item to view its history.
      </Typography>
    );
  }

  return (
    <CrudDataGrid<History>
      rows={history ?? []}
      columns={columns}
      loading={isLoading}
      getRowId={(r) => r.idRoadmapHistory}
      density="compact"
      autoHeight
      disableRowSelectionOnClick
      pagination
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        pagination: { paginationModel: { pageSize: 10 } },
        sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
      }}
    />
  );
}
